import type { OutOfStockProduct } from "../../stockList/checkoutV2/lib/useValidateCheckout";

type InactiveProduct = {
  id: number;
  name: string;
};

type UnifiedCheckoutPayAlertsProps = {
  placeOrderError: string;
  isValidatingPaymentReturn: boolean;
  paymentReturnError: string;
  reconciliationError: boolean;
  reconciliationFetchError: boolean;
  showProductErrors: boolean;
  inactiveProducts: InactiveProduct[];
  outOfStockProducts: OutOfStockProduct[];
  supplierName: string;
  onReviewUpdatedBaskets: () => void;
  onRemoveInactiveProduct: (_productId: number) => void;
  onRemoveOutOfStockProduct: (_productId: number) => void;
  onBackToMarket: () => void;
};

export const UnifiedCheckoutPayAlerts = ({
  placeOrderError,
  isValidatingPaymentReturn,
  paymentReturnError,
  reconciliationError,
  reconciliationFetchError,
  showProductErrors,
  inactiveProducts,
  outOfStockProducts,
  supplierName,
  onReviewUpdatedBaskets,
  onRemoveInactiveProduct,
  onRemoveOutOfStockProduct,
  onBackToMarket,
}: UnifiedCheckoutPayAlertsProps) => (
  <>
    {placeOrderError && (
      <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
        {placeOrderError}
      </div>
    )}

    {isValidatingPaymentReturn && (
      <div className="rounded-lg bg-purple-50 p-4 text-sm text-purple-800">
        Confirming your payment…
      </div>
    )}

    {paymentReturnError && (
      <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
        {paymentReturnError}
      </div>
    )}

    {reconciliationError && (
      <div className="flex flex-col gap-3 rounded-lg bg-amber-50 p-4 text-sm text-amber-900">
        <span>
          We updated your basket because the ordering cut-off passed and one or
          more items now have a revised delivery date.
        </span>
        <button
          type="button"
          className="self-start font-semibold underline"
          onClick={onReviewUpdatedBaskets}
        >
          Review updated baskets
        </button>
      </div>
    )}

    {reconciliationFetchError && (
      <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
        We couldn't refresh your basket before ordering. Check your connection
        and try again.
      </div>
    )}

    {showProductErrors && inactiveProducts.length > 0 && (
      <div className="flex flex-col gap-3 rounded-lg bg-red-50 p-4 text-sm text-red-700">
        {inactiveProducts.map((product) => (
          <div
            key={product.id}
            className="flex items-center justify-between gap-3"
          >
            <span>
              {supplierName} has made {product.name} no longer available for
              sale.
            </span>
            <button
              type="button"
              className="font-semibold text-red-800 underline"
              onClick={() => onRemoveInactiveProduct(product.id)}
            >
              Remove item
            </button>
          </div>
        ))}
      </div>
    )}

    {showProductErrors && outOfStockProducts.length > 0 && (
      <div className="flex flex-col gap-3 rounded-lg bg-red-50 p-4 text-sm text-red-700">
        <strong>Some items are out of stock</strong>
        {outOfStockProducts.map((product) => (
          <div
            key={product.id}
            className="flex items-center justify-between gap-3"
          >
            <span>
              {product.name}
              {product.description ? ` • ${product.description}` : ""}
            </span>
            <button
              type="button"
              className="font-semibold text-red-800 underline"
              onClick={() => onRemoveOutOfStockProduct(product.id)}
            >
              Remove item
            </button>
          </div>
        ))}
        <button
          type="button"
          className="self-start font-semibold text-red-800 underline"
          onClick={onBackToMarket}
        >
          Go back to market
        </button>
      </div>
    )}
  </>
);
